import React, { ComponentPropsWithoutRef } from 'react';

import { AnalyticsProps, buildAnalyticsAttributes } from 'support/helpers/analytics/analytics';
import { cn } from 'support/helpers/cn/cn';
import { WithRef } from 'support/types/withRef';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';
import { LoadingLogo } from 'components/Loading/LoadingLogo';

export type ButtonType = {
  type?: 'submit' | 'button' | 'reset';
  variant?:
    | 'primary'
    | 'secondary'
    | 'minimal'
    | 'minimal-emphasized'
    | 'text'
    | 'text-muted'
    | 'secondary-danger'
    | 'minimal-danger'
    | 'ai';
  size?: 'extra-small' | 'small' | 'base' | 'large' | 'extra-large';
  LeftIcon?: React.ComponentType<any>;
  RightIcon?: React.ComponentType<any>;
  iconOnly?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  pressed?: boolean;
  grouped?: boolean;
  testId?: string;
  as?: React.ElementType;
} & ComponentPropsWithoutRef<'button'> &
  AnalyticsProps;

const classes = {
  button: {
    variant: {
      primary:
        'border border-transparent shadow-xs text-white bg-black hover:bg-procuros-green-999 hover:text-procuros-green-300 active:bg-procuros-green-999 active:text-procuros-green-600 disabled:text-gray-300 disabled:border-gray-100 disabled:bg-gray-100',
      secondary:
        'border border-gray-300 shadow-xs text-gray-700 bg-white hover:bg-procuros-green-50 hover:text-procuros-green-500 active:bg-procuros-green-50 active:text-procuros-green-900 disabled:text-gray-300 disabled:border-gray-100 disabled:bg-gray-100',
      minimal:
        'text-gray-700 hover:bg-procuros-green-50 hover:text-procuros-green-600 active:bg-procuros-green-50 active:text-procuros-green-900 disabled:bg-transparent disabled:text-gray-300',
      'minimal-emphasized':
        'shadow-none text-procuros-green-600 hover:text-procuros-green-500 hover:bg-procuros-green-50 active:bg-procuros-green-50 active:text-procuros-green-900  disabled:bg-transparent disabled:text-gray-300',
      'minimal-danger':
        'shadow-none text-red-600 hover:text-red-500 hover:bg-red-50 active:bg-red-50 active:text-red-900 disabled:bg-transparent disabled:text-gray-300',
      text: 'text-procuros-green-600 hover:text-procuros-green-400 p-0!',
      'text-muted': 'text-gray-500 underline underline-offset-2 hover:text-gray-700 disabled:text-gray-300 p-0!',
      'secondary-danger':
        'border border-red-300 shadow-xs text-red-700 bg-white hover:bg-red-50 hover:text-red-600 active:bg-red-50 active:text-red-900 disabled:text-gray-300 disabled:border-gray-100 disabled:bg-gray-100',
      ai: 'ai-gradient-border border border-transparent shadow-xs text-gray-700 hover:text-gray-900 active:text-gray-900 disabled:text-gray-300 disabled:border-gray-100 disabled:shadow-none disabled:bg-gray-100',
    },
    pressed: {
      primary: 'bg-procuros-green-999! text-procuros-green-300',
      secondary: 'bg-procuros-green-50! text-procuros-green-500',
      minimal: 'bg-procuros-green-50! text-procuros-green-600',
      'minimal-emphasized': 'bg-procuros-green-50! text-procuros-green-500',
      'minimal-danger': 'text-red-500',
      text: 'text-procuros-green-400',
      'text-muted': 'text-gray-700',
      'secondary-danger': 'bg-red-50! text-red-600',
      ai: 'bg-gray-100! text-gray-900',
    },
    size: {
      'extra-small': 'px-2.5 py-1.5 text-xs rounded-sm',
      small: 'px-3 py-2 text-sm leading-4 rounded-md',
      base: 'px-4 py-2 text-sm rounded-md',
      large: 'px-4 py-2 text-base rounded-md',
      'extra-large': 'px-6 py-3 text-base rounded-md',
    },
    loading: 'text-transparent!',
  },
  leftIcon: {
    size: {
      'extra-small': '-ml-0.5 mr-2 h-4 w-4',
      small: '-ml-0.5 mr-2 h-4 w-4',
      base: '-ml-1 mr-2 h-5 w-5',
      large: '-ml-1 mr-3 h-5 w-5',
      'extra-large': '-ml-1 mr-3 h-5 w-5',
    },
  },
  rightIcon: {
    size: {
      'extra-small': 'ml-2 -mr-0.5 h-4 w-4',
      small: 'ml-2 -mr-0.5 h-4 w-4',
      base: 'ml-2 -mr-1 h-5 w-5',
      large: 'ml-3 -mr-1 h-5 w-5',
      'extra-large': 'ml-3 -mr-1 h-5 w-5',
    },
  },
};

const Button = ({
  type = 'button',
  variant = 'primary',
  size = 'base',
  iconOnly,
  LeftIcon,
  RightIcon,
  onClick,
  loading,
  fullWidth,
  className,
  disabled,
  children,
  testId,
  pressed,
  grouped,
  analyticsId,
  analyticsProperties,
  ref,
  ...otherProps
}: WithRef<ButtonType, HTMLButtonElement>) => {
  const HTMLTag = otherProps.as || 'button';

  return (
    <Tooltip placement="bottom">
      <TooltipTrigger>
        <HTMLTag
          {...otherProps}
          {...buildAnalyticsAttributes({ analyticsId, analyticsProperties })}
          disabled={disabled || loading}
          type={type}
          onClick={onClick}
          className={cn(
            fullWidth ? 'flex w-full justify-center' : 'inline-flex w-fit',
            classes.button.variant[variant],
            classes.button.size[size],
            'relative min-w-max items-center font-medium focus-visible:outline-hidden',
            pressed ? classes.button.pressed[variant] : '',
            grouped
              ? '-ml-px rounded-none first:ml-0 first:rounded-l-md last:rounded-r-md'
              : ' focus-visible:ring-2 focus-visible:ring-procuros-green-400 focus-visible:ring-offset-2',
            loading ? classes.button.loading : '',
            className ?? '',
          )}
          data-testid={testId}
          ref={ref}
        >
          {!!LeftIcon && (
            <LeftIcon className={cn(classes.leftIcon.size[size], iconOnly ? 'mx-0' : '')} aria-hidden="true" />
          )}
          {iconOnly ? null : children}
          {!!RightIcon && (
            <RightIcon className={cn(classes.rightIcon.size[size], iconOnly ? 'mx-0' : '')} aria-hidden="true" />
          )}
          {loading && (
            <span className="absolute top-1/2 left-1/2 size-5 origin-center -translate-1/2 text-gray-300">
              <LoadingLogo />
            </span>
          )}
        </HTMLTag>
      </TooltipTrigger>
      {iconOnly && children ? <TooltipContent>{children}</TooltipContent> : null}
    </Tooltip>
  );
};

export { Button };
